import { Chip, TextField } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { Breadcrumbs, Drawer, Grid } from '@mui/material';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect, ConnectedProps, useSelector } from 'react-redux';
import { compose } from 'redux';
import { useParams } from 'react-router-dom';
import { getSystemPromptCategoriesSelector } from '@redux/caregiverAssistant/caregiverAssistantSelectors';
import { getSystemPromptCategoriesThunk } from '@redux/caregiverAssistant/caregiverAssistantThunks';
import { CaregiverCategoryProgram, SystemPromptCategory } from '@redux/caregiverAssistant/caregiverAssistantTypes';
import { postNewTaskThunk } from '@redux/tasks/tasksThunks';
import { TaskCreationPayloadType, TaskLogsType } from '@redux/tasks/tasksTypes';
import { ReduxState } from '@redux/types';
import { useAlert } from 'components/AlertInfoGlobal/useAlert';
import { Button } from 'components/Button/Button';
import { CloseIconButton } from 'components/Button/CloseButton/CloseIconButton';
import { ConfirmationDialogV2 } from 'components/ConfirmationDialog/ConfirmationDialogV2';
import { DropDownMenu } from 'components/DropDownMenu/DropDownMenu';
import { ENDS_SELECT_OPTIONS, FREQUENCY_SELECT_OPTIONS, FrequencyType } from 'components/FrequencySelectBlock/FrequencySelectBlock';
import { TriggerPreviewOptions, TriggerTypes } from 'components/TriggerSelectBlock/TriggerSelectBlock';
import { Text } from 'components/Typography';
import CancelTaskModal from 'containers/Tasks/components/CancelTaskModal/CancelTaskModal';
import { TaskLogs } from 'containers/Tasks/components/TaskLogs/TaskLogs';
import { Color } from 'utils/color';
import style from './CaregiverAssistantDrawer.scss';
import { CategoryCard } from './components/CategoryCard/CategoryCard';
import { PromptCard } from './components/PromptCard/PromptCard';
import { SchedulePromptForm } from './components/Schedule/ScheduleForm';
import {
  WarningCircleIcon,
  DotsThreeIcon,
  XSquareIcon,
  LeftChevronIcon,
  MagnifyingGlassIcon,
  CalendarIcon,
  MapPinIcon,
  ChatIcon,
  RepeatIcon
} from 'components/Icons';
import { CATEGORY_ICON_MAP } from './declarations/constants';
import { CategoryType } from './declarations/types';
import { getDeviceModeByPersonIdSelector, getPersonFromPersonByIdSelector } from '@redux/person/personSelector';
import createFrontEndError from 'utils/createFrontEndError';

enum ASSISTANT_CREATION_STAGE {
  CATEGORY = 'category',
  PROMPT = 'prompt',
  SCHEDULE = 'schedule',
  DETAILS = 'details'
}

type TaskCreationConfirmationProps = {
  title: string;
  description: string;
  proceedText: string;
  closeText: string;
  proceedFunction: () => void;
};

type CaregiverAssistantDrawerProps = {
  open: boolean;
  onClose: () => void;
  onSuccessSubmit?: () => void;
};

type ScheduledTaskDetails = {
  id: string;
  logs: TaskLogsType[];
  scheduledDate: string; // the date at the task was scheduled
  scheduledTimeStart: string; // the time at the task was scheduled
  scheduledTimeEnd: string;
  frequency: FrequencyType;
  promptText: string;
  trigger: TriggerTypes;
  responseRequired?: boolean;
  finishDate?: string;
};

type Props = ConnectedProps<typeof connectRedux> & CaregiverAssistantDrawerProps;

const dateConfig: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric'
};

const timeConfig: Intl.DateTimeFormatOptions = {
  hour: 'numeric',
  minute: '2-digit'
};

const mapPromptCategories = (systemPromptCategories: SystemPromptCategory[]): CategoryType[] => {
  if (!systemPromptCategories.length) return [];

  return systemPromptCategories
    .map((category) => {
      const iconSchema = CATEGORY_ICON_MAP[category.name.toLowerCase()];
      return {
        id: category.id,
        label: category.name,
        description: category.description,
        prompts: category.programs,
        status: category.status,
        icon: iconSchema ? iconSchema.icon : <DotsThreeIcon/>,
        iconColor: iconSchema? iconSchema.iconColor : null
      };
    });
};

const formatDate = (value?: string | Date | null, formatConfig: Intl.DateTimeFormatOptions = dateConfig) => {
  if (!value) return '';
  const dateValue = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(dateValue.getTime())) return '';
  return dateValue.toLocaleDateString(undefined, formatConfig);
};

const formatTime = (value?: string | Date | null, formatConfig: Intl.DateTimeFormatOptions = timeConfig) => {
  if (!value) return '';
  const dateValue = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(dateValue.getTime())) return '';
  return dateValue.toLocaleTimeString(undefined, formatConfig);
};

const CaregiverAssistantDrawer: React.FC<Props> = ({
  open,
  onClose,
  onSuccessSubmit,
  postNewTask,
  getSystemPromptCategories,
  systemPromptCategories,
  selectPerson
}) => {
  const { userId } = useParams<{ userId: string }>();
  const person = userId && selectPerson(userId);
  const deviceMode = useSelector((state: ReduxState) => (userId ? getDeviceModeByPersonIdSelector(state, userId) : undefined));
  
  const { showAlertInfo, showAlertWarning } = useAlert();

  const [creationStage, setCreationStage] = useState<ASSISTANT_CREATION_STAGE>(ASSISTANT_CREATION_STAGE.CATEGORY);
  const [taskCreatedSuccessfully, setTaskCreatedSuccessfully] = useState(false);
  const [confirmDialogProps, setConfirmDialogProps] = useState<Nullable<TaskCreationConfirmationProps>>(null);
  const [cancelTaskOptions, setCancelTaskOptions] = useState<{ id: string; isSingleCancel: boolean } | null>(null);
  const [categorySelected, setCategorySelected] = useState<Nullable<CategoryType>>(null);
  const [searchPromptText, setSearchPromptText] = useState<string>('');
  const [promptSelected, setPromptSelected] = useState<Nullable<CaregiverCategoryProgram>>(null);
  const [filteredPrompts, setFilteredPrompts] = useState<CaregiverCategoryProgram[] | null>(null);
  const [taskDetails, setTaskDetails] = useState<Nullable<ScheduledTaskDetails>>(null);
  const [isScheduleFormValid, setIsScheduleFormValid] = useState(false);

  const categories = useMemo(() => mapPromptCategories(systemPromptCategories), [systemPromptCategories]);

  useEffect(() => {
    if (open && systemPromptCategories.length === 0) {
      getSystemPromptCategories();
    }
  }, [open, systemPromptCategories.length, getSystemPromptCategories]);

  const debouncedSearch = useRef(
    debounce((value: string, prompts?: CaregiverCategoryProgram[]) => {
      if (value.length < 3) {
        setFilteredPrompts(null);
        return;
      }

      const results = (prompts || []).filter((p) => {
        const testString = `${p.name.toLowerCase()} ${p.subType?.toLowerCase()} ${p.tags?.join(" ").toLowerCase()}`;
        return testString.includes(value.toLowerCase());
      });
      setFilteredPrompts(results);
    }, 600)
  ).current;

  // Fetch initial Prompt list
  useEffect(() => {
    if (categorySelected) {
      setCreationStage(ASSISTANT_CREATION_STAGE.PROMPT);
    }
  }, [categorySelected]);

  useEffect(() => {
    debouncedSearch(searchPromptText, categorySelected?.prompts);
    return () => {
      debouncedSearch.cancel();
    };
  }, [searchPromptText, categorySelected, debouncedSearch]);

  // Restore full prompts for selected category when search is cleared
  useEffect(() => {
    if (searchPromptText === '' && categorySelected) {
      setFilteredPrompts(null);
    }
  }, [searchPromptText, categorySelected]);

  const onCloseDrawer = () => {
    setConfirmDialogProps(null);
    resetCreationStage();
    onClose();
    
    if (taskCreatedSuccessfully) {
      onSuccessSubmit?.();
    }
    setTaskCreatedSuccessfully(false);
  };

  const resetCreationStage = () => {
    setCreationStage(ASSISTANT_CREATION_STAGE.CATEGORY);
    setPromptSelected(null);
    setSearchPromptText('');
    setCategorySelected(null);
    setIsScheduleFormValid(false);
  };

  const closeDrawerHandler = () => {
    if (creationStage !== ASSISTANT_CREATION_STAGE.DETAILS) {
      setConfirmDialogProps({
        title: 'You have unsaved changes',
        description: 'If you close the assistant now, the changes will be lost. Do you wish to exit?',
        proceedText: 'Yes, exit',
        closeText: 'Go back',
        proceedFunction: onCloseDrawer
      });
    } else {
      onCloseDrawer();
    }
  };

  const returnToStage = () => {
    if (creationStage === ASSISTANT_CREATION_STAGE.PROMPT) {
      setCategorySelected(null);
      setCreationStage(ASSISTANT_CREATION_STAGE.CATEGORY);
    }
    if (creationStage === ASSISTANT_CREATION_STAGE.SCHEDULE) {
      setCreationStage(ASSISTANT_CREATION_STAGE.PROMPT);
      setPromptSelected(null);
      setIsScheduleFormValid(false);
    }
  };

  const selectPrompt = useCallback((p) => {
    setPromptSelected(p);
    setIsScheduleFormValid(false);
    setCreationStage(ASSISTANT_CREATION_STAGE.SCHEDULE);
  }, []);

  // Ref used to call the child's exposed submitForm() method
  const formRef = useRef<{ submitScheduleForm: () => void }>(null);
  const schedulePrompt = () => {
    formRef.current?.submitScheduleForm();
  };

  const handleSubmitAddNewTask = async (task: TaskCreationPayloadType) => {
    try {
      const response = await postNewTask(task);
      const data = response?.data ?? response ?? {};
      const resolvedRepeat = (data?.frequency as FREQUENCY_SELECT_OPTIONS) ?? task.frequency ?? FREQUENCY_SELECT_OPTIONS.no_repeat;
      const resolvedEnds =
        resolvedRepeat === FREQUENCY_SELECT_OPTIONS.no_repeat
          ? undefined
          : ((data?.frequencyEnds ?? task.frequencyEnds ?? ENDS_SELECT_OPTIONS.after) as ENDS_SELECT_OPTIONS);
      const resolvedAfterRaw = data?.endsAfterOccurrences ?? task.endsAfterOccurrences;
      const resolvedAfter = resolvedAfterRaw != null ? Number(resolvedAfterRaw) : undefined;
      const resolvedEndsOn = data?.frequencyEndsOn ?? data?.endsOnDate ?? task.endsOnDate;
      const resolvedDate = resolvedEndsOn ? new Date(resolvedEndsOn) : undefined;
      const scheduledFor = data?.scheduledForDateTime ?? task.scheduledForDateTime;
      const expiration = data?.expirationDateTime ?? data?.expiredForDateTime ?? task.expiredForDateTime ?? scheduledFor;

      const taskValues = {
        id: data?.id ?? '',
        promptText: data?.deliverableMessage ?? data?.rawMessage ?? task.taskMessage ?? '',
        logs: data?.logs,
        scheduledDate: formatDate(scheduledFor),
        scheduledTimeStart: formatTime(scheduledFor),
        scheduledTimeEnd: formatTime(expiration),
        frequency: {
          repeat: resolvedRepeat,
          ends: resolvedEnds,
          after: resolvedAfter,
          date: resolvedDate
        },
        trigger: data?.triggerCondition ?? task.triggerCondition,
        responseRequired: data?.responseRequired ?? promptSelected?.insightRequired ?? false,
        finishDate: resolvedEnds === ENDS_SELECT_OPTIONS.date ? formatDate(resolvedDate ?? resolvedEndsOn) : ''
      };

      setTaskDetails(taskValues);
      setCreationStage(ASSISTANT_CREATION_STAGE.DETAILS);
      setTaskCreatedSuccessfully(true);
      showAlertInfo({
        message: 'Task scheduled successfully!'
      });
    } catch (err) {
      const error = createFrontEndError(err);
      showAlertWarning({
        message: error.message
      });
    }
  };

  const cancelTask = () => {
    setCancelTaskOptions({ id: '4343434', isSingleCancel: true });
    onCloseDrawer();
  };

  const hideConfirmationModal = useCallback(() => {
    setCancelTaskOptions(null);
  }, [postNewTask, showAlertInfo, showAlertWarning, onSuccessSubmit, setCreationStage]);

  const isSearchActive = searchPromptText.length >= 3;
  const promptListInUse = isSearchActive ? filteredPrompts ?? [] : categorySelected?.prompts;
  const showNoMatches = isSearchActive && filteredPrompts !== null && filteredPrompts.length === 0;

  return (
    <>
      <Drawer anchor="right" open={open} onClose={onCloseDrawer} variant="temporary">
        <div className={style.mainContainer}>
          <div className={style.drawerContainer}>
            <div className={style.assistantContainer}>
              <div
                className={`${style.drawerHeader} ${
                  creationStage === ASSISTANT_CREATION_STAGE.DETAILS ? style.drawerHeaderActions : ''
                }`}
              >
                {creationStage !== ASSISTANT_CREATION_STAGE.DETAILS ? (
                  <Breadcrumbs className={style.breadcrumbs}>
                    <Text className={creationStage === ASSISTANT_CREATION_STAGE.CATEGORY ? style.activeBreadcrumb : style.inactiveBreadcrumb}>Category</Text>
                    <Text className={creationStage === ASSISTANT_CREATION_STAGE.PROMPT ? style.activeBreadcrumb : style.inactiveBreadcrumb}>Prompt</Text>
                    <Text className={creationStage === ASSISTANT_CREATION_STAGE.SCHEDULE ? style.activeBreadcrumb : style.inactiveBreadcrumb}>Schedule</Text>
                  </Breadcrumbs>
                ) : (
                  <div className={style.actionsHeader}>
                    <DropDownMenu
                      menuText="Actions"
                      menuItems={[
                        {
                          text: 'Cancel',
                          icon: () => <XSquareIcon width={16} height={16} />,
                          onClick: () => cancelTask()
                        }
                      ]}
                    />
                  </div>
                )}
                <CloseIconButton containerClassName={style.closeButton} onClick={closeDrawerHandler} />
              </div>

              <div className={style.assistantConfigurationContainer}>
                {creationStage == ASSISTANT_CREATION_STAGE.CATEGORY && (
                  <>
                    <div className={style.titles}>
                      <Text variant="h5" weight={500}>
                        Select conversation category
                      </Text>
                      <Text className={style.subtitle}>Choose a category to see conversation prompts</Text>
                    </div>
                    <Grid container spacing={2}>
                      {categories.map((c, i) => {
                        const Icon = c.icon ? <c.icon color={c.iconColor} /> : <DotsThreeIcon />
                        return (
                          <Grid key={i} size={{ xs: 12, md: 6 }}>
                            <CategoryCard label={`${c.label}`} icon={Icon} onClick={() => setCategorySelected(c)} programsAmount={c.prompts?.length}/>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </>
                )}
                {creationStage === ASSISTANT_CREATION_STAGE.PROMPT && (
                  <>
                    <div className={style.titles}>
                      <Text variant="h5" weight={500}>
                        {categorySelected?.label}
                      </Text>
                      <Text className={style.subtitle}>Select a conversation for Luna to have with your loved one</Text>
                    </div>
                    <TextField
                      variant="outlined"
                      placeholder="Search..."
                      className={style.searchField}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSearchPromptText(value);
                      }}
                      margin="dense"
                      size="small"
                      InputProps={{
                        startAdornment: <SearchIcon fontSize="small" color="disabled" className={style.searchIcon} />
                      }}
                    />
                    <div className={style.promptList}>
                      {promptListInUse?.map((p, i) => (
                        <PromptCard key={i} title={p.name || p.subType} description={p.description} clickHandler={() => selectPrompt(p)} />
                      ))}
                    </div>
                    {showNoMatches && (
                      <div className={style.noMatchesContainer}>
                        <div className={style.noMatchesIcon}>
                          <MagnifyingGlassIcon height={24} width={24} color={Color.GRAY_BLUE} />
                        </div>
                        <Text className={style.noMatchesText}>No matches found</Text>
                      </div>
                    )}
                  </>
                )}
                {creationStage === ASSISTANT_CREATION_STAGE.SCHEDULE && promptSelected && (
                  <>
                    <div className={style.titles}>
                      <Text variant="h5" weight={500}>
                        {promptSelected.name}
                      </Text>
                      <Text className={style.subtitle}>Choose when Luna should have this conversation</Text>
                    </div>

                    <SchedulePromptForm
                      ref={formRef}
                      prompt={promptSelected}
                      person={person ? person : null}
                      deviceMode={deviceMode}
                      onValidityChange={setIsScheduleFormValid}
                      onSubmit={(values) => handleSubmitAddNewTask(values)}
                    />
                  </>
                )}
                {creationStage === ASSISTANT_CREATION_STAGE.DETAILS && promptSelected && taskDetails && (
                  <div className={style.detailsContainer}>
                    <div className={style.header}>
                      <Chip label="Scheduled" className={style.statusChip} />
                      <Text weight={500} className={style.title}>
                        {promptSelected.subType || promptSelected.name}
                      </Text>
                    </div>
                    <div className={style.detailRow}>
                      <CalendarIcon width={24} height={24} />
                      <Text color={Color.GRAY}>{`${taskDetails.scheduledDate} at ${taskDetails.scheduledTimeStart} - ${taskDetails.scheduledTimeEnd}`}</Text>
                    </div>
                    <div className={style.detailRow}>
                      <RepeatIcon width={24} height={24} />
                      {taskDetails.frequency.repeat === FREQUENCY_SELECT_OPTIONS.no_repeat && <Text color={Color.GRAY}>No Repeat</Text>}
                      {taskDetails.frequency.repeat === FREQUENCY_SELECT_OPTIONS.every_day && <Text color={Color.GRAY}>Daily</Text>}
                      {taskDetails.frequency.repeat === FREQUENCY_SELECT_OPTIONS.every_week && <Text color={Color.GRAY}>Weekly</Text>}
                      {taskDetails.frequency.repeat === FREQUENCY_SELECT_OPTIONS.every_month && <Text color={Color.GRAY}>Monthly</Text>}
                      {taskDetails.frequency.ends === ENDS_SELECT_OPTIONS.after && <Text color={Color.GRAY}>{`(${taskDetails.frequency.after}x)`}</Text>}
                      {taskDetails?.finishDate && <Text color={Color.GRAY}>{` until ${taskDetails.finishDate}`}</Text>}
                    </div>
                    <div className={style.detailRow}>
                      <MapPinIcon width={24} height={24} />
                      <Text color={Color.GRAY}>{TriggerPreviewOptions[taskDetails.trigger]}</Text>
                    </div>
                    <div className={style.detailRow}>
                      <ChatIcon width={24} height={24} />
                      <Text color={Color.GRAY}>{taskDetails.responseRequired ? 'Response required' : 'No response required'}</Text>
                    </div>
                    <div className={style.taskLogSection}>
                      <TaskLogs
                        logsList={taskDetails.logs}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className={style.drawerFooter}>
                {(creationStage === ASSISTANT_CREATION_STAGE.SCHEDULE || creationStage === ASSISTANT_CREATION_STAGE.PROMPT) && (
                  <Button variant="outlined" onClick={returnToStage} startIcon={<LeftChevronIcon height={16} width={16} />}>
                    Back
                  </Button>
                )}
                {creationStage === ASSISTANT_CREATION_STAGE.SCHEDULE && (
                  <Button variant="contained" onClick={schedulePrompt} disabled={!isScheduleFormValid}>
                    Schedule
                  </Button>
                )}
                {creationStage === ASSISTANT_CREATION_STAGE.DETAILS && (
                  <div className={style.scheduleMoreSection}>
                    <Button variant="contained" onClick={resetCreationStage}>
                      Schedule another
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <ConfirmationDialogV2
          open={!!confirmDialogProps}
          icon={WarningCircleIcon}
          title={confirmDialogProps?.title}
          description={confirmDialogProps?.description}
          proceedEvent={() => confirmDialogProps?.proceedFunction()}
          proceedEventText={confirmDialogProps?.proceedText}
          closeEvent={() => setConfirmDialogProps(null)}
          closeEventText={confirmDialogProps?.closeText}
        />
        <CancelTaskModal
          taskId={cancelTaskOptions?.id}
          visible={!!cancelTaskOptions?.id}
          isSingleCancel={cancelTaskOptions?.isSingleCancel}
          onConfirmPress={cancelTask}
          onClosePress={hideConfirmationModal}
        />
      </Drawer>
    </>
  );
};

const connectRedux = connect(
  (state: ReduxState) => ({
    systemPromptCategories: getSystemPromptCategoriesSelector(state),
    selectPerson: (userId: string) => getPersonFromPersonByIdSelector(state, userId)
  }),
  {
    getSystemPromptCategories: getSystemPromptCategoriesThunk,
    postNewTask: postNewTaskThunk
  }
);

export default compose(connectRedux)(CaregiverAssistantDrawer);
